import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import dayjs from 'dayjs'
import req from '@sportninja/common/api/request'
import { resolvePastWindowStart } from '@sportninja/common/features/event/resolvePastWindowStart'
import { orgScheduleReducer, createInitialState } from './reducer'
import {
  OrgScheduleContextValue,
  ActionType,
  Schedule,
  Team,
  Venue,
  Facility,
  TimePeriod,
  SortOrder,
  Organization,
} from './types'
import { Dayjs } from 'dayjs'

const OrgScheduleContext = createContext<OrgScheduleContextValue | undefined>(
  undefined
)

interface OrgScheduleContextProviderProps {
  children: React.ReactNode
  orgId: string
}

export const OrgScheduleContextProvider: React.FC<
  OrgScheduleContextProviderProps
> = ({ children, orgId }) => {
  const initialState = useMemo(() => createInitialState(orgId), [orgId])
  const [state, dispatch] = useReducer(orgScheduleReducer, initialState)

  // Derived values
  const activeOrgId = state.selectedSubOrg?.id || orgId
  const hasSubOrgs = state.subOrganizations.length > 0

  /**
   * Mark filters as loaded on mount
   */
  useEffect(() => {
    dispatch({ type: ActionType.SET_FILTERS_LOADED, payload: true })
  }, [])

  /**
   * Construct filter parameters for API calls
   */
  const getFilterParams = useCallback(() => {
    return {
      schedule_id: state.selectedSchedule?.id,
      team_id: state.selectedTeam?.id,
      venue_id: state.selectedVenue?.id,
      facility_id: state.selectedFacility?.id,
      recursive: 1,
      per_page: 45,
    }
  }, [
    state.selectedSchedule?.id,
    state.selectedTeam?.id,
    state.selectedVenue?.id,
    state.selectedFacility?.id,
  ])

  /**
   * Get date range based on selected time period or custom date range
   */
  const getDateRange = useCallback(() => {
    // Custom date range takes precedence
    if (state.selectedDateRange) {
      return {
        starts_after: state.selectedDateRange[0].startOf('day').format(),
        starts_before: state.selectedDateRange[1].endOf('day').format(),
        order: state.selectedSortOrder,
      }
    }

    // Time period based ranges
    switch (state.selectedTimePeriod) {
      case 'TODAY': {
        const date = dayjs().startOf('day')
        return {
          starts_after: date.format(),
          starts_before: date.add(1, 'day').format(),
          order: state.selectedSortOrder,
        }
      }
      case 'UPCOMING': {
        return {
          starts_after: dayjs().startOf('day').format(),
          starts_before: dayjs().startOf('day').add(180, 'day').format(),
          order: state.selectedSortOrder,
        }
      }
      case 'PAST': {
        return {
          // Anchored to the selected schedule's own start when one is picked,
          // else floored at 2 years. Previously open-ended, which asked a
          // long-running competition for its entire history to render one tab.
          starts_after: resolvePastWindowStart(
            state.selectedSchedule?.starts_at
          ).format(),
          starts_before: dayjs().startOf('day').format(),
          order: state.selectedSortOrder,
        }
      }
      default:
        return {
          starts_after: dayjs().startOf('day').format(),
          starts_before: dayjs().startOf('day').add(1, 'day').format(),
          order: state.selectedSortOrder,
        }
    }
  }, [
    state.selectedDateRange,
    state.selectedTimePeriod,
    state.selectedSortOrder,
    state.selectedSchedule?.starts_at,
  ])

  /**
   * Fetch games based on current filters
   */
  const fetchGames = useCallback(async () => {
    try {
      dispatch({ type: ActionType.SET_LOADING_GAMES, payload: true })

      const filterParams = getFilterParams()
      const dateRange = getDateRange()

      const query = {
        ...filterParams,
        ...dateRange,
        page: 1,
      } as any

      const { data, meta } = await req(`/organizations/${activeOrgId}/games`, {
        query,
      })

      dispatch({
        type: ActionType.SET_GAMES,
        payload: {
          games: data || [],
          pagination: meta?.pagination || null,
        },
      })
    } catch (error) {
      console.error('Error fetching games:', error)
      dispatch({
        type: ActionType.SET_GAMES,
        payload: { games: [], pagination: null },
      })
    } finally {
      dispatch({ type: ActionType.SET_LOADING_GAMES, payload: false })
    }
  }, [getFilterParams, getDateRange])

  /**
   * Fetch games for a specific page (pagination)
   */
  const fetchGamesPage = useCallback(
    async (page: number) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_GAMES, payload: true })

        const filterParams = getFilterParams()
        const dateRange = getDateRange()

        const query = {
          ...filterParams,
          ...dateRange,
          page,
          per_page: 45,
        } as any

        const { data, meta } = await req(
          `/organizations/${activeOrgId}/games`,
          { query }
        )

        dispatch({
          type: ActionType.SET_GAMES,
          payload: {
            games: data || [],
            pagination: meta?.pagination || null,
          },
        })
      } catch (error) {
        console.error('Error fetching games for page:', error)
        dispatch({
          type: ActionType.SET_GAMES,
          payload: { games: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_GAMES, payload: false })
      }
    },
    [getFilterParams, getDateRange]
  )

  /**
   * Fetch more games (pagination)
   */
  const fetchMoreGames = useCallback(async () => {
    if (!state.gamesPagination) {
      return
    }
    if (
      state.gamesPagination.current_page >= state.gamesPagination.total_pages
    ) {
      return
    }

    try {
      const filterParams = getFilterParams()
      const dateRange = getDateRange()

      const query = {
        ...filterParams,
        ...dateRange,
        page: state.gamesPagination.current_page + 1,
      } as any

      const { data, meta } = await req(`/organizations/${activeOrgId}/games`, {
        query,
      })

      dispatch({
        type: ActionType.APPEND_GAMES,
        payload: {
          games: data || [],
          pagination: meta?.pagination || null,
        },
      })
    } catch (error) {
      console.error('Error fetching more games:', error)
    }
  }, [state.gamesPagination, getFilterParams, getDateRange])

  /**
   * Fetch schedules for the organization
   */
  const fetchSchedules = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_SCHEDULES, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'starts_at',
          direction: 'desc',
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req(
          `/organizations/${activeOrgId}/schedules`,
          {
            query,
          }
        )

        dispatch({
          type: ActionType.SET_SCHEDULES,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('Error fetching schedules:', error)
        dispatch({
          type: ActionType.SET_SCHEDULES,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_SCHEDULES, payload: false })
      }
    },
    [activeOrgId]
  )

  /**
   * Fetch teams for the organization
   */
  const fetchTeams = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_TEAMS, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'name_full',
          direction: 'asc',
        }

        if (state.selectedSchedule) {
          query.schedule_id = state.selectedSchedule.id
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req(
          `/organizations/${activeOrgId}/teams`,
          {
            query,
          }
        )

        dispatch({
          type: ActionType.SET_TEAMS,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('Error fetching teams:', error)
        dispatch({
          type: ActionType.SET_TEAMS,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_TEAMS, payload: false })
      }
    },
    [activeOrgId, state.selectedSchedule]
  )

  /**
   * Fetch venues for the organization
   */
  const fetchVenues = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_VENUES, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'name_full',
          direction: 'asc',
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req(
          `/organizations/${activeOrgId}/venues`,
          {
            query,
          }
        )

        dispatch({
          type: ActionType.SET_VENUES,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('Error fetching venues:', error)
        dispatch({
          type: ActionType.SET_VENUES,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_VENUES, payload: false })
      }
    },
    [activeOrgId]
  )

  /**
   * Fetch more schedules (pagination)
   */
  const fetchMoreSchedules = useCallback(async () => {
    if (!state.schedulesPagination) {
      return
    }
    if (
      state.schedulesPagination.current_page >=
      state.schedulesPagination.total_pages
    ) {
      return
    }
    if (state.appendingSchedules) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_SCHEDULES, payload: true })

      const query: Record<string, any> = {
        page: state.schedulesPagination.current_page + 1,
        sort: 'starts_at',
        direction: 'desc',
      }

      if (state.schedulesSearchQuery) {
        query.query = state.schedulesSearchQuery
      }

      const { data, meta } = await req(
        `/organizations/${activeOrgId}/schedules`,
        {
          query,
        }
      )

      dispatch({
        type: ActionType.APPEND_SCHEDULES,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('Error fetching more schedules:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_SCHEDULES, payload: false })
    }
  }, [
    state.schedulesPagination,
    state.appendingSchedules,
    state.schedulesSearchQuery,
    activeOrgId,
  ])

  /**
   * Fetch more teams (pagination)
   */
  const fetchMoreTeams = useCallback(async () => {
    if (!state.teamsPagination) {
      return
    }
    if (
      state.teamsPagination.current_page >= state.teamsPagination.total_pages
    ) {
      return
    }
    if (state.appendingTeams) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_TEAMS, payload: true })

      const query: Record<string, any> = {
        page: state.teamsPagination.current_page + 1,
        sort: 'name_full',
        direction: 'asc',
      }

      if (state.selectedSchedule) {
        query.schedule_id = state.selectedSchedule.id
      }

      if (state.teamsSearchQuery) {
        query.query = state.teamsSearchQuery
      }

      const { data, meta } = await req(`/organizations/${activeOrgId}/teams`, {
        query,
      })

      dispatch({
        type: ActionType.APPEND_TEAMS,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('Error fetching more teams:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_TEAMS, payload: false })
    }
  }, [
    state.teamsPagination,
    state.appendingTeams,
    state.selectedSchedule,
    state.teamsSearchQuery,
    activeOrgId,
  ])

  /**
   * Fetch more venues (pagination)
   */
  const fetchMoreVenues = useCallback(async () => {
    if (!state.venuesPagination) {
      return
    }
    if (
      state.venuesPagination.current_page >= state.venuesPagination.total_pages
    ) {
      return
    }
    if (state.appendingVenues) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_VENUES, payload: true })

      const query: Record<string, any> = {
        page: state.venuesPagination.current_page + 1,
        sort: 'name_full',
        direction: 'asc',
      }

      if (state.venuesSearchQuery) {
        query.query = state.venuesSearchQuery
      }

      const { data, meta } = await req(`/organizations/${activeOrgId}/venues`, {
        query,
      })

      dispatch({
        type: ActionType.APPEND_VENUES,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('Error fetching more venues:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_VENUES, payload: false })
    }
  }, [
    state.venuesPagination,
    state.appendingVenues,
    state.venuesSearchQuery,
    activeOrgId,
  ])

  /**
   * Search schedules (reset and fetch with search query)
   */
  const searchSchedules = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_SCHEDULES_SEARCH_QUERY, payload: query })
      fetchSchedules(query)
    },
    [fetchSchedules]
  )

  /**
   * Search teams (reset and fetch with search query)
   */
  const searchTeams = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_TEAMS_SEARCH_QUERY, payload: query })
      fetchTeams(query)
    },
    [fetchTeams]
  )

  /**
   * Search venues (reset and fetch with search query)
   */
  const searchVenues = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_VENUES_SEARCH_QUERY, payload: query })
      fetchVenues(query)
    },
    [fetchVenues]
  )

  /**
   * Fetch game dates to determine smart default time period
   */
  const fetchDates = useCallback(async () => {
    try {
      dispatch({ type: ActionType.SET_LOADING_DATES, payload: true })

      const today = dayjs()
      const startsAfter = today.subtract(6, 'month').format()
      const startsBefore = today.add(6, 'month').format()

      const { data } = await req('/games/dates', {
        query: {
          organization_id: activeOrgId,
          starts_after: startsAfter,
          starts_before: startsBefore,
        },
      })

      // Check if today has games
      const todayFormatted = today.format('YYYY-MM-DD')
      const hasTodayGames = data?.includes(todayFormatted) || false

      // Check if there are any upcoming games
      const hasUpcomingGames =
        data?.some((dateStr: string) => dayjs(dateStr).isAfter(today)) || false

      // Set smart default time period
      const defaultPeriod: TimePeriod = hasTodayGames
        ? 'TODAY'
        : hasUpcomingGames
        ? 'UPCOMING'
        : 'PAST'

      dispatch({
        type: ActionType.SET_DATES,
        payload: {
          dates: data || [],
          hasTodayGames,
          hasUpcomingGames,
          defaultTimePeriod: defaultPeriod,
        },
      })
    } catch (error) {
      console.error('Error fetching dates:', error)
      dispatch({
        type: ActionType.SET_DATES,
        payload: {
          dates: [],
          hasTodayGames: false,
          hasUpcomingGames: false,
        },
      })
    } finally {
      dispatch({ type: ActionType.SET_LOADING_DATES, payload: false })
    }
  }, [activeOrgId])

  /**
   * Fetch sub organizations for the parent organization
   */
  const fetchSubOrganizations = useCallback(
    async (search?: string) => {
      try {
        dispatch({ type: ActionType.SET_LOADING_SUB_ORGS, payload: true })

        const query: Record<string, any> = {
          page: 1,
          sort: 'name_full',
          direction: 'asc',
          membership_type: 'permission',
        }

        if (search) {
          query.query = search
        }

        const { data, meta } = await req(`/organizations/${orgId}/children`, {
          query,
        })

        dispatch({
          type: ActionType.SET_SUB_ORGANIZATIONS,
          payload: { data: data || [], pagination: meta?.pagination || null },
        })
      } catch (error) {
        console.error('Error fetching sub organizations:', error)
        dispatch({
          type: ActionType.SET_SUB_ORGANIZATIONS,
          payload: { data: [], pagination: null },
        })
      } finally {
        dispatch({ type: ActionType.SET_LOADING_SUB_ORGS, payload: false })
      }
    },
    [orgId]
  )

  /**
   * Fetch more sub organizations (pagination)
   */
  const fetchMoreSubOrgs = useCallback(async () => {
    if (!state.subOrgsPagination) {
      return
    }
    if (
      state.subOrgsPagination.current_page >=
      state.subOrgsPagination.total_pages
    ) {
      return
    }
    if (state.appendingSubOrgs) {
      return
    }

    try {
      dispatch({ type: ActionType.SET_APPENDING_SUB_ORGS, payload: true })

      const query: Record<string, any> = {
        page: state.subOrgsPagination.current_page + 1,
        sort: 'name_full',
        direction: 'asc',
        membership_type: 'permission',
      }

      if (state.subOrgsSearchQuery) {
        query.query = state.subOrgsSearchQuery
      }

      const { data, meta } = await req(`/organizations/${orgId}/children`, {
        query,
      })

      dispatch({
        type: ActionType.APPEND_SUB_ORGANIZATIONS,
        payload: { data: data || [], pagination: meta?.pagination || null },
      })
    } catch (error) {
      console.error('Error fetching more sub organizations:', error)
    } finally {
      dispatch({ type: ActionType.SET_APPENDING_SUB_ORGS, payload: false })
    }
  }, [
    state.subOrgsPagination,
    state.appendingSubOrgs,
    state.subOrgsSearchQuery,
    orgId,
  ])

  /**
   * Search sub organizations (reset and fetch with search query)
   */
  const searchSubOrgs = useCallback(
    (query: string) => {
      dispatch({ type: ActionType.SET_SUB_ORGS_SEARCH_QUERY, payload: query })
      fetchSubOrganizations(query)
    },
    [fetchSubOrganizations]
  )

  /**
   * Auto-fetch games when filters change
   */
  useEffect(() => {
    // Only fetch games after dates are loaded (smart default is set)
    if (!state.filtersLoaded || !state.datesLoaded) {
      return
    }

    fetchGames()
  }, [
    state.filtersLoaded,
    state.datesLoaded,
    state.selectedSchedule?.id,
    state.selectedTeam?.id,
    state.selectedVenue?.id,
    state.selectedFacility?.id,
    state.selectedDateRange,
    state.selectedTimePeriod,
    state.selectedSortOrder,
    fetchGames,
  ])

  /**
   * Fetch sub organizations on mount
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }

    fetchSubOrganizations()
  }, [state.filtersLoaded, fetchSubOrganizations])

  /**
   * Fetch filter options on mount
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }

    fetchSchedules()
    fetchTeams()
    fetchVenues()
  }, [state.filtersLoaded])

  /**
   * Re-fetch filter options when sub org changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    // Skip on initial load (activeOrgId === orgId and no sub org selected)
    if (!state.selectedSubOrg && activeOrgId === orgId) {
      return
    }

    fetchSchedules()
    fetchTeams()
    fetchVenues()
  }, [state.selectedSubOrg?.id])

  /**
   * Fetch dates once to determine smart default time period
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    if (state.datesLoaded) {
      return
    }

    fetchDates()
  }, [state.filtersLoaded, state.datesLoaded, fetchDates])

  /**
   * Re-fetch teams when schedule changes
   */
  useEffect(() => {
    if (!state.filtersLoaded) {
      return
    }
    fetchTeams()
  }, [state.selectedSchedule?.id])

  // Setter functions
  const setSelectedSchedule = useCallback((schedule: Schedule | null) => {
    dispatch({ type: ActionType.SET_SELECTED_SCHEDULE, payload: schedule })
  }, [])

  const setSelectedTeam = useCallback((team: Team | null) => {
    dispatch({ type: ActionType.SET_SELECTED_TEAM, payload: team })
  }, [])

  const setSelectedVenue = useCallback((venue: Venue | null) => {
    dispatch({ type: ActionType.SET_SELECTED_VENUE, payload: venue })
  }, [])

  const setSelectedFacility = useCallback((facility: Facility | null) => {
    dispatch({ type: ActionType.SET_SELECTED_FACILITY, payload: facility })
  }, [])

  const setSelectedDateRange = useCallback((range: [Dayjs, Dayjs] | null) => {
    dispatch({ type: ActionType.SET_SELECTED_DATE_RANGE, payload: range })
  }, [])

  const setSelectedTimePeriod = useCallback((period: TimePeriod) => {
    dispatch({ type: ActionType.SET_SELECTED_TIME_PERIOD, payload: period })
  }, [])

  const setSelectedSortOrder = useCallback((order: SortOrder) => {
    dispatch({ type: ActionType.SET_SELECTED_SORT_ORDER, payload: order })
  }, [])

  const setSelectedSubOrg = useCallback((org: Organization | null) => {
    dispatch({ type: ActionType.SET_SELECTED_SUB_ORG, payload: org })
  }, [])

  const clearFilter = useCallback(
    (
      filterType:
        | 'schedule'
        | 'team'
        | 'venue'
        | 'facility'
        | 'dateRange'
        | 'timePeriod'
        | 'subOrg'
    ) => {
      dispatch({ type: ActionType.CLEAR_FILTER, payload: filterType })
    },
    []
  )

  const clearAllFilters = useCallback(() => {
    dispatch({ type: ActionType.CLEAR_ALL_FILTERS })
  }, [])

  const value: OrgScheduleContextValue = {
    ...state,
    setSelectedSchedule,
    setSelectedTeam,
    setSelectedVenue,
    setSelectedFacility,
    setSelectedDateRange,
    setSelectedTimePeriod,
    setSelectedSortOrder,
    setSelectedSubOrg,
    hasSubOrgs,
    fetchGames,
    fetchGamesPage,
    fetchMoreGames,
    fetchSchedules,
    fetchTeams,
    fetchVenues,
    fetchMoreSchedules,
    fetchMoreTeams,
    fetchMoreVenues,
    fetchMoreSubOrgs,
    searchSchedules,
    searchTeams,
    searchVenues,
    searchSubOrgs,
    clearFilter,
    clearAllFilters,
  }

  return (
    <OrgScheduleContext.Provider value={value}>
      {children}
    </OrgScheduleContext.Provider>
  )
}

export const useOrgScheduleContext = () => {
  const context = useContext(OrgScheduleContext)
  if (context === undefined) {
    throw new Error(
      'useOrgScheduleContext must be used within OrgScheduleContextProvider'
    )
  }
  return context
}
